import { useRef, useState, type KeyboardEvent } from "react";
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from "@diametral/design-system/react";

import { useTechnologies } from "../api/projects";
import { useTranslation } from "../lib/i18n";
import { useDebounced } from "../lib/useDebounced";
import { RemovableTag } from "./RemovableTag";

/**
 * Technologies of a project: removable tags plus a text field suggesting the names already
 * known (/api/technologies). A name that is not in the list is accepted too: the backend
 * creates it.
 */
export function TechnologiesField({
  value,
  onChange,
  id,
  label,
  disabled,
}: {
  value: string[];
  onChange: (technologies: string[]) => void;
  id?: string;
  label?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const highlighted = useRef<string | undefined>(undefined);
  const debounced = useDebounced(text.trim(), 200);
  const suggestions = useTechnologies(debounced, !disabled);
  const known = new Set(value.map((v) => v.toLocaleLowerCase("fr")));
  const items = (suggestions.data ?? []).filter((name) => !known.has(name.toLocaleLowerCase("fr")));

  function add(raw: string) {
    const name = raw.trim();
    setText("");
    if (!name || known.has(name.toLocaleLowerCase("fr"))) return;
    onChange([...value, name]);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      // A highlighted suggestion is committed by the autocomplete itself (item press).
      if (event.key === "Enter" && highlighted.current !== undefined) return;
      event.preventDefault();
      add(text);
    } else if (event.key === "Backspace" && text === "" && value.length) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="ada-stack ada-stack--tight">
      {value.length ? (
        <div className="ada-cluster">
          {value.map((name) => (
            <RemovableTag
              key={name}
              disabled={disabled}
              removeLabel={t("Remove {{name}}", { name })}
              onRemove={() => onChange(value.filter((v) => v !== name))}
            >
              {name}
            </RemovableTag>
          ))}
        </div>
      ) : null}
      <Autocomplete
        items={items}
        filter={null}
        value={text}
        onValueChange={(next: string, details: { reason: string }) => {
          if (details.reason === "item-press") add(next);
          else setText(next);
        }}
        onItemHighlighted={(item: string | undefined) => {
          highlighted.current = item;
        }}
        disabled={disabled}
      >
        <AutocompleteInput
          id={id}
          aria-label={label ?? t("Technologies")}
          placeholder={t("Add a technology (Enter to confirm)")}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
        <AutocompleteContent>
          <AutocompleteEmpty>
            {text.trim() ? t("Enter to add « {{name}} »", { name: text.trim() }) : t("No suggestions")}
          </AutocompleteEmpty>
          <AutocompleteList>
            {(name: string) => (
              <AutocompleteItem key={name} value={name}>
                {name}
              </AutocompleteItem>
            )}
          </AutocompleteList>
        </AutocompleteContent>
      </Autocomplete>
    </div>
  );
}
